function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const defaultOptions = {
  backgroundColor: 'rgb(255, 255, 0)',
  fontStyle: 'italic',
  color: 'inherit',
};

// 特殊文字を素の形と entity 形両方にマッチする regex 片に展開（例: 'Q&A' → 'Q(?:&amp;|&)A'）
const HTML_ENTITY_OR_RAW = {
  '&': '(?:&amp;|&)',
  '<': '(?:&lt;|<)',
  '>': '(?:&gt;|>)',
  '"': '(?:&quot;|")',
  "'": "(?:&#39;|')",
};

// `&NAME;` の保護解除判定に使う entity 名（`&` を除いた中身）
const ENTITY_NAME_FOR_CHAR = {
  '&': 'amp',
  '<': 'lt',
  '>': 'gt',
  '"': 'quot',
  "'": '#39',
};

/**
 * Highlights keywords in HTML content
 * @param {string} htmlContent - The HTML string to search within
 * @param {string[]} keywords - Array of keywords to highlight
 * @param {Object} options - Styling options for the highlight
 * @returns {string} The HTML string with keywords highlighted
 */
export function highlightKeywordsInHtml(htmlContent, keywords, options = {}) {
  const { backgroundColor, fontStyle, color } = {
    ...defaultOptions,
    ...options,
  };

  const keywordsArray = Array.isArray(keywords) ? keywords : [];
  const uniqueKeywords = Array.from(
    new Set(
      keywordsArray.filter((k) => typeof k === 'string' && k.length > 0),
    ),
  );
  if (uniqueKeywords.length === 0) return htmlContent;

  // 長さ降順: alternation は leftmost-alternative-first のため、
  // "A" と "AB" の両方がある時に "AB" を優先させる
  const sortedKeywords = uniqueKeywords.sort((a, b) => b.length - a.length);
  const pattern = sortedKeywords
    .map(escapeRegExp)
    .map((k) => k.replace(/[&<>"']/g, (c) => HTML_ENTITY_OR_RAW[c]))
    .join('|');
  // 特殊文字を含むキーワードがある entity は、そのキーワード側にマッチさせるため保護対象から外す。
  // ただし他キーワードが entity 内部に誤マッチして `&am<mark>p</mark>;` のように壊す場合
  // （例: `['A&B', 'p']`）は除外しない。
  const excludedEntities = Object.entries(ENTITY_NAME_FOR_CHAR)
    .filter(([char, name]) => {
      const hasChar = uniqueKeywords.some((k) => k.includes(char));
      if (!hasChar) return false;
      // 特殊文字単体がキーワードなら、展開パターンが entity 全体を先頭から消費するため誤マッチは起こらない
      if (uniqueKeywords.includes(char)) return true;
      // `&NAME;` の `NAME;` 部分にキーワードが部分文字列として現れる場合のみ競合とみなす。
      // 逆方向の `lowerK.includes(name)` は `'amplifier'` のような長いキーワードを誤検知するため不採用。
      const nameWithSemicolon = `${name};`;
      const hasConflict = uniqueKeywords.some((k) => {
        if (k.includes(char)) return false;
        // regex は `gi` フラグ。大文字小文字を区別しない比較が必要
        const lowerK = k.toLowerCase();
        return nameWithSemicolon.includes(lowerK);
      });
      return !hasConflict;
    })
    .map(([, name]) => name);
  const entityPattern = excludedEntities.length > 0
    ? `&(?!(?:${excludedEntities.join('|')});)[a-zA-Z0-9#]+;`
    : '&[a-zA-Z0-9#]+;';
  // 既存 HTML タグ (<...>) と HTML エンティティ (&amp; など) をキャプチャして保護しつつ、
  // キーワードにマッチさせる
  const regex = new RegExp(`(<[^>]+>|${entityPattern})|(${pattern})`, 'gi');

  // 1パス置換: 挿入後の <mark> 属性文字列に次キーワードが再マッチするバグを防ぐ
  return htmlContent.replace(regex, (_match, tagMatch, keywordMatch) => {
    if (tagMatch) return tagMatch; // マッチしたのがタグまたはエンティティの場合はそのまま返す
    return `<mark style="background-color: ${backgroundColor}; font-style: ${fontStyle}; color: ${color};">${keywordMatch}</mark>`;
  });
}

/**
 * Highlights keywords in all elements matching the selector within containers
 * @param {string} containerSelector - Selector for container elements (e.g., '.js-highlight-container')
 * @param {string} elementSelector - Selector for elements to highlight within (e.g., '.js-highlight')
 * @param {Function} getKeywords - Function to extract keywords from container and element
 * @param {Object} options - Styling options for the highlight
 */
export function highlightElementsInContainers(
  containerSelector,
  elementSelector,
  getKeywords,
  options = {},
) {
  const containers = document.querySelectorAll(containerSelector);

  containers.forEach((container) => {
    const elements = container.querySelectorAll(elementSelector);

    elements.forEach((element) => {
      const keywords = getKeywords(container, element);
      if (keywords.length === 0) return;

      const originalHtml = element.innerHTML;
      const highlightedHtml = highlightKeywordsInHtml(
        originalHtml,
        keywords,
        options,
      );

      if (highlightedHtml !== originalHtml) {
        element.innerHTML = highlightedHtml;
      }
    });
  });
}
